/**
 * Dispatch task lifecycle.
 *
 * WHY tasks have their own machine:
 * The whole promise of the system — "tự động điều phối, chuyển nhiệm vụ nếu
 * nhân viên không nhận" — lives in the edges of this graph. A task that nobody
 * accepts must be re-offered, then escalated, then reassigned, and each of
 * those must be a distinct recorded status so KPI can tell "nhân viên từ chối"
 * apart from "hệ thống hết giờ chờ". Collapsing them into a generic PENDING
 * would silently delete the evidence the KPI and payroll modules are built on.
 *
 * Statuses mirror `tasks.status` in `database/migrations/0008_dispatch_kpi.sql`.
 */

import { StateMachine } from './state-machine.js';
import type { Transition } from './state-machine.js';

/** Task statuses exactly as constrained in `tasks.status`. */
export const TASK_STATUSES = [
  'CREATED',
  'OFFERED',
  'ACCEPTED',
  'IN_PROGRESS',
  'WAITING',
  'COMPLETED',
  'REJECTED',
  'REASSIGNED',
  'ESCALATED',
  'CANCELLED',
  'FAILED',
  'EXPIRED',
] as const;

/** Union of task statuses. */
export type TaskStatus = (typeof TASK_STATUSES)[number];

/** Task events. */
export const TASK_EVENTS = [
  'OFFER',
  'ACCEPT',
  'REJECT',
  'START',
  'PAUSE',
  'RESUME',
  'COMPLETE',
  'FAIL',
  'REASSIGN',
  'ESCALATE',
  'CANCEL',
  'EXPIRE',
] as const;

/** Union of task events. */
export type TaskEvent = (typeof TASK_EVENTS)[number];

/**
 * Statuses a task can still be dispatched out of.
 *
 * REJECTED, REASSIGNED, ESCALATED and EXPIRED are *not* endings — they are
 * outcomes of one attempt. The dispatcher re-enters the offer loop from all of
 * them, which is why they carry outgoing edges below.
 */
const REDISPATCHABLE: readonly TaskStatus[] = ['CREATED', 'REJECTED', 'REASSIGNED', 'ESCALATED', 'EXPIRED'];

/** Statuses in which a task is owned by a specific employee right now. */
const ASSIGNED: readonly TaskStatus[] = ['ACCEPTED', 'IN_PROGRESS', 'WAITING'];

function fanIn(
  froms: readonly TaskStatus[],
  event: TaskEvent,
  to: TaskStatus,
  extra: { permission?: string; guardName?: string } = {},
): Transition<TaskStatus, TaskEvent>[] {
  return froms.map((from) => ({ from, event, to, ...extra }));
}

/**
 * The task lifecycle graph.
 *
 * Notable modelling decisions:
 * - OFFER carries no permission: it is the dispatcher Durable Object acting on
 *   `dispatch_rules`, not a person. Manual hand-outs go through REASSIGN, which
 *   *is* guarded.
 * - ACCEPT is reachable from every redispatchable status, including CREATED —
 *   that is `task_types.allow_self_claim`: a free staff member may grab work
 *   before the dispatcher gets to it. The compare-and-swap on `claim_version`
 *   in the DB is what makes the race safe; this graph only says it is legal.
 * - COMPLETE is allowed straight from ACCEPTED so a five-second task ("mang
 *   thêm đũa") does not need a pointless START tap on a busy floor.
 * - EXPIRE applies both to an unanswered offer and to an accepted task that
 *   blew past `due_at` — same word, same recorded status, distinguished by
 *   which state it came from.
 */
export const TASK_TRANSITIONS: readonly Transition<TaskStatus, TaskEvent>[] = [
  ...fanIn(REDISPATCHABLE, 'OFFER', 'OFFERED'),
  { from: 'OFFERED', event: 'OFFER', to: 'OFFERED', guardName: 'nextOfferRound' },

  ...fanIn([...REDISPATCHABLE, 'OFFERED'], 'ACCEPT', 'ACCEPTED', { permission: 'task.accept' }),
  { from: 'OFFERED', event: 'REJECT', to: 'REJECTED', permission: 'task.reject', guardName: 'requiresReason' },

  { from: 'ACCEPTED', event: 'START', to: 'IN_PROGRESS', permission: 'task.accept' },
  { from: 'IN_PROGRESS', event: 'PAUSE', to: 'WAITING', permission: 'task.accept', guardName: 'requiresReason' },
  { from: 'WAITING', event: 'RESUME', to: 'IN_PROGRESS', permission: 'task.accept' },

  ...fanIn(['ACCEPTED', 'IN_PROGRESS'], 'COMPLETE', 'COMPLETED', {
    permission: 'task.complete',
    guardName: 'evidenceIfRequired',
  }),
  ...fanIn(ASSIGNED, 'FAIL', 'FAILED', { permission: 'task.complete', guardName: 'requiresReason' }),

  // Reassignment keeps the task row (and its history) and hands it to someone
  // else; the previous assignment is closed in `task_assignments`.
  ...fanIn([...ASSIGNED, 'OFFERED', 'REJECTED', 'ESCALATED'], 'REASSIGN', 'REASSIGNED', {
    permission: 'task.reassign',
    guardName: 'requiresReason',
  }),

  // Escalation is time-driven (`task_escalation_rules`), so it is unguarded and
  // reachable from anything still open — including EXPIRED, which is exactly
  // the case a shift leader must be told about.
  ...fanIn(['CREATED', 'OFFERED', 'REJECTED', 'REASSIGNED', 'EXPIRED', ...ASSIGNED], 'ESCALATE', 'ESCALATED'),

  ...fanIn(
    ['CREATED', 'OFFERED', 'REJECTED', 'REASSIGNED', 'ESCALATED', 'EXPIRED', ...ASSIGNED],
    'CANCEL',
    'CANCELLED',
    { permission: 'task.cancel', guardName: 'requiresReason' },
  ),

  ...fanIn(['CREATED', 'OFFERED', ...ASSIGNED], 'EXPIRE', 'EXPIRED'),
];

/**
 * The shared task lifecycle.
 *
 * Only COMPLETED, CANCELLED and FAILED are terminal. EXPIRED is deliberately
 * left open: an expired task still needs someone to do the work, and the
 * dispatcher re-offers or escalates it.
 */
export const taskMachine = new StateMachine<TaskStatus, TaskEvent>(TASK_TRANSITIONS, {
  name: 'nhiệm vụ',
  terminalStates: ['COMPLETED', 'CANCELLED', 'FAILED'],
});

/** Is this task still the dispatcher's problem? Drives the realtime task board. */
export function isTaskOpen(status: TaskStatus): boolean {
  return !taskMachine.isTerminal(status);
}

/**
 * Is the task currently counted against an employee's active workload?
 *
 * The dispatcher uses this to respect `max_concurrent_tasks` — an offered task
 * does NOT count, because an employee may hold several offers and accept one.
 */
export function isTaskHeldByEmployee(status: TaskStatus): boolean {
  return status === 'ACCEPTED' || status === 'IN_PROGRESS' || status === 'WAITING';
}
